import type { LucideIcon } from "lucide-react";
import { AppWindow, FileArchive, FileQuestion, FileText, Moon, Trash2 } from "lucide-react";

export interface ThoughtFile {
    id: string;
    name: string;
    icon: LucideIcon;
    meta: string;
    lines: string[];
    altTake: string;
}

export const THOUGHT_FILES: ThoughtFile[] = [
    {
        id: "perfect-first",
        name: "perfect_first_attempt.zip",
        icon: FileArchive,
        meta: "archive · 0 versions shipped",
        lines: [
            "archive unavailable.",
            "reason: the author insists on making at least three improvements before calling anything finished.",
        ],
        altTake: "counterpoint: the fourth improvement is always where the real bugs hide.",
    },
    {
        id: "bug-free",
        name: "bug_free_code.exe",
        icon: AppWindow,
        meta: "application · never launched",
        lines: [
            "could not launch.",
            "error: perfection not found. the executable and the author are both works in progress.",
        ],
        altTake: "have you tried turning it off and calling it version 1.0?",
    },
    {
        id: "sleep-early",
        name: "i_will_sleep_early.txt",
        icon: Moon,
        meta: "text · 01:47 AM",
        lines: [
            "file status: repeatedly modified. last modified: yesterday, 01:47 AM.",
            "execution status: failed. reason: 'one last thing' turned into five last things.",
        ],
        altTake: "have you considered that 01:47 is technically early (morning)?",
    },
    {
        id: "life-plan",
        name: "life_plan_final.pdf",
        icon: FileText,
        meta: "document · version: unclear",
        lines: [
            "multiple versions detected: life_plan_final.pdf, life_plan_FINAL_v2.pdf, life_plan_final_for_real.pdf.",
            "latest version: still under review.",
        ],
        altTake: "have you tried merging all three branches and calling it growth?",
    },
    {
        id: "small-project",
        name: "one_small_project_that_stays_small.zip",
        icon: FileArchive,
        meta: "archive · 47 files",
        lines: [
            "archive grew. initial estimate: 2 files.",
            "actual: 47 files, 3 folders, and a README no one reads.",
        ],
        altTake: "have you considered that 'small' was always a rough estimate?",
    },
    {
        id: "tab-open",
        name: "why_is_this_tab_open.md",
        icon: FileQuestion,
        meta: "markdown · investigation ongoing",
        lines: [
            "investigation ongoing. this tab has survived three browser restarts and one laptop migration.",
            "closing it is considered risky. relevance: unknown. loyalty: absolute.",
        ],
        altTake: "have you tried asking the tab why IT thinks it's open?",
    },
];

export interface RecycleItem {
    id: string;
    name: string;
    meta: string;
    popup: string[];
    egg?: boolean;
}

export const RECYCLE_ITEMS: RecycleItem[] = [
    {
        id: "resume-v1",
        name: "resume_v1.pdf",
        meta: "deleted · format: standard",
        popup: ["this is one of several standard-format resumes. none survived review.", "see also: error 404."],
    },
    {
        id: "one-tab",
        name: "i_will_stop_at_one_tab.exe",
        meta: "deleted · never ran",
        popup: ["the executable refused to run out of self-respect.", "some files know they're fiction."],
    },
    {
        id: "sleep-sys",
        name: "sleep_schedule.sys",
        meta: "corrupted · daily",
        popup: ["file corrupts itself every night around 01:47.", "recovery attempt: scheduled. perpetually."],
    },
    {
        id: "todo-bak",
        name: "todo.txt.bak",
        meta: "last modified: never",
        popup: ["a backup of a file that was never written.", "the purest form of procrastination: planned productivity."],
    },
    {
        id: "egg-file",
        name: "definitely_not_an_easter_egg.txt",
        meta: "system file · do not read. seriously.",
        popup: [
            "you read it anyway. incredible. this is exactly the kind of behavior that gets badges around here.",
            "+1 easter egg found.",
        ],
        egg: true,
    },
];

export interface Quest {
    title: string;
    status: string;
    xp: number;
    color: string;
    desc: string;
    note?: string;
}

export const QUESTS: Quest[] = [
    {
        title: "main quest: shipping software",
        status: "active",
        xp: 78,
        color: "#2563EB",
        desc: "junior software developer at EG India — vision-model document pipelines on AWS Bedrock, FastAPI + pgvector backends, Next.js review workspaces, and a very real .NET 10 + Angular 21 estimation platform on the side.",
    },
    {
        title: "side quest: saxophone",
        status: "ongoing",
        xp: 60,
        color: "#EAB308",
        desc: "i play the saxophone. progress is measured in long notes and neighbour patience.",
        note: "honest status: long notes are steadier than they were, vibrato is still a work in progress, and the reed decides how the session goes before i do. practice happens in short bursts on weeknights and longer ones when a bug has beaten me and i need a different kind of problem.",
    },
    {
        title: "side quest: professional eating appreciation",
        status: "daily",
        xp: 95,
        color: "#FF6B6B",
        desc: "i enjoy food and watching mukbang videos. i prefer eating to cooking. someone has to keep the appreciation side of the industry alive.",
    },
    {
        title: "side quest: toastmasters",
        status: "in progress",
        xp: 45,
        color: "#A855F7",
        desc: "developing public speaking skills one evaluated speech at a time.",
        note: "what it actually taught me: structure beats confidence. prepared speeches are the easy part — table topics, where you get a subject and about two minutes, are where the real debugging happens. the evaluations are the feature: honest feedback, every single session, with nowhere to hide. it has made me noticeably better at explaining technical work to people who don't want the architecture diagram.",
    },
    {
        title: "side quest: the childhood curriculum",
        status: "completed",
        xp: 100,
        color: "#10B981",
        desc: "swimming, karate, carnatic music, bharatanatyam, western dance, drawing, badminton, yakshagana. rewards kept: all of them.",
    },
];

export const CHILDHOOD = ["swimming", "karate", "carnatic music", "bharatanatyam", "western dance", "drawing", "badminton", "yakshagana"];

export const STICKY_BASE = [
    { text: "reminder: the tabs are load-bearing", color: "#FEF08A", pos: "right-8 top-24 -rotate-3 hidden md:block" },
    { text: "sax practice today. the neighbours said 'brave'", color: "#F3E8FF", pos: "right-44 top-44 rotate-2 hidden lg:block" },
];

export const STICKY_OVERTHINK = [
    { text: "did i close that loop? which loop? THE loop?", color: "#FFE8E8", pos: "right-10 top-56 rotate-1 hidden lg:block" },
    { text: "wait — what if we refactored the refactor?", color: "#FEF9C3", pos: "right-56 top-16 rotate-3 hidden xl:block" },
];

export const STICKY_CHAOS = [
    { text: "RESEARCH CASCADE IN PROGRESS. do not tidy up.", color: "#FFEDD5", pos: "right-24 top-72 -rotate-2 hidden xl:block" },
];

export const PERF_ROWS: { name: string; usage: string; note: string }[] = [
    { name: "switching between tabs", usage: "84%", note: "chronic" },
    { name: "rerendering decisions", usage: "61/s", note: "peak: 61/s" },
    { name: "pretending to read docs", usage: "37%", note: "skimming counts" },
    { name: "actual compiling", usage: "12%", note: "finally" },
    { name: "sleep daemon", usage: "0%", note: "not found" },
];

export const RECYCLE_NOTE_ICON = Trash2;
